"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import { PixelText } from "@/components/ui/pixel-text";

type SaveResult = { ok: boolean; slot?: string };

type Props = {
  open: boolean;
  onResume: () => void;
  onSave: () => Promise<SaveResult>;
  onLoad: () => void;
  onSettings: () => void;
  onParty: () => void;
  onBag: () => void;
  onSurggieCenter?: () => void;
  onPlugins?: () => void;
  onDelegate?: () => void;
  onDex: () => void;
  onQuitToTitle: () => void;
  // Optional side-panel data — fall back to "—" when absent.
  playTime?: string;
  mapName?: string;
  partyCount?: number;
};

type MenuId =
  | "resume"
  | "party"
  | "bag"
  | "surggieCenter"
  | "plugins"
  | "delegate"
  | "dex"
  | "save"
  | "settings"
  | "quit";

const ITEMS: { id: MenuId; label: string }[] = [
  { id: "resume", label: "Resume" },
  { id: "party", label: "Party" },
  { id: "bag", label: "Bag" },
  { id: "surggieCenter", label: "Surggie Center" },
  { id: "plugins", label: "Plugins" },
  { id: "delegate", label: "Delegate" },
  { id: "dex", label: "Dex" },
  { id: "save", label: "Save" },
  { id: "settings", label: "Settings" },
  { id: "quit", label: "Quit to Title" },
];

// Palette (PIXEL_STYLE.md — earth + accent only).
const CREAM = "#f5ecd6";
const INK = "#1a1a1a";
const WALNUT = "#5c4a30";
const SAND = "#b89968";
const ALERT = "#c54a4a";

type SurggiesAudio = { playSfx?: (name: string) => void };

function playSfx(name: string) {
  if (typeof window === "undefined") return;
  const audio = (window as unknown as { surggiesAudio?: SurggiesAudio })
    .surggiesAudio;
  audio?.playSfx?.(name);
}

type SaveStatus =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "ok"; slot: string }
  | { kind: "error" };

export function PauseMenu({
  open,
  onResume,
  onSave,
  onLoad: _onLoad,
  onSettings,
  onParty,
  onBag,
  onSurggieCenter,
  onPlugins,
  onDelegate,
  onDex,
  onQuitToTitle,
  playTime,
  mapName,
  partyCount,
}: Props) {
  const [cursor, setCursor] = useState(0);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>({ kind: "idle" });
  const prevOpen = useRef(false);
  const okTimer = useRef<number | null>(null);

  // Reset cursor + status when the menu opens; fire open/close sfx on transitions.
  useEffect(() => {
    if (open && !prevOpen.current) {
      playSfx("menu_open");
      setCursor(0);
      setSaveStatus({ kind: "idle" });
    } else if (!open && prevOpen.current) {
      playSfx("menu_close");
    }
    prevOpen.current = open;
  }, [open]);

  // Clear any pending save-ok timer on unmount/close.
  useEffect(() => {
    return () => {
      if (okTimer.current !== null) {
        window.clearTimeout(okTimer.current);
        okTimer.current = null;
      }
    };
  }, []);

  const move = useCallback((delta: number) => {
    setCursor((c) => {
      const next = (c + delta + ITEMS.length) % ITEMS.length;
      return next;
    });
    playSfx("menu_move");
  }, []);

  const confirm = useCallback(async (index?: number) => {
    // Explicit index for mouse clicks — the cursor state in this closure is
    // stale when setCursor(i) and confirm() fire in the same click handler.
    const item = ITEMS[index ?? cursor];
    playSfx("menu_select");
    switch (item.id) {
      case "resume":
        onResume();
        return;
      case "party":
        onParty();
        return;
      case "bag":
        onBag();
        return;
      case "surggieCenter":
        onSurggieCenter?.();
        return;
      case "plugins":
        onPlugins?.();
        return;
      case "delegate":
        onDelegate?.();
        return;
      case "dex":
        onDex();
        return;
      case "settings":
        onSettings();
        return;
      case "quit":
        onQuitToTitle();
        return;
      case "save": {
        setSaveStatus({ kind: "saving" });
        try {
          const res = await onSave();
          if (res.ok) {
            const slot = res.slot ?? "auto";
            setSaveStatus({ kind: "ok", slot });
            if (okTimer.current !== null) window.clearTimeout(okTimer.current);
            okTimer.current = window.setTimeout(() => {
              setSaveStatus({ kind: "idle" });
              okTimer.current = null;
            }, 1500);
          } else {
            setSaveStatus({ kind: "error" });
          }
        } catch {
          setSaveStatus({ kind: "error" });
        }
        return;
      }
    }
  }, [
    cursor,
    onResume,
    onParty,
    onBag,
    onSurggieCenter,
    onPlugins,
    onDelegate,
    onDex,
    onSettings,
    onQuitToTitle,
    onSave,
  ]);

  // Keyboard handling.
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key;
      if (
        key === "ArrowUp" ||
        key === "ArrowDown" ||
        key === "Enter" ||
        key === "z" ||
        key === "Z" ||
        key === "x" ||
        key === "X" ||
        key === "Escape"
      ) {
        e.preventDefault();
      }

      switch (key) {
        case "ArrowUp":
          move(-1);
          break;
        case "ArrowDown":
          move(1);
          break;
        case "Enter":
        case "z":
        case "Z":
          void confirm();
          break;
        case "x":
        case "X":
        case "Escape":
          onResume();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, move, confirm, onResume]);

  if (!open) return null;

  const showSaveLine =
    saveStatus.kind === "ok" ||
    saveStatus.kind === "error" ||
    saveStatus.kind === "saving";

  return (
    <div
      // Backdrop — clicks are intentionally ignored (only buttons can close).
      aria-hidden={false}
      role="dialog"
      aria-label="Pause menu"
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(26, 26, 26, 0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 60,
        imageRendering: "pixelated",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "0.75rem",
          alignItems: "flex-start",
        }}
      >
        {/* Menu panel */}
        <div
          style={{
            width: 360,
            backgroundColor: CREAM,
            border: `2px solid ${INK}`,
            boxShadow: `inset 1px 1px 0 ${CREAM}, inset -1px -1px 0 ${WALNUT}`,
            padding: "1rem 1.25rem",
          }}
        >
          <PixelText
            as="div"
            glow="none"
            size="lg"
            className="!text-[#1a1a1a] !tracking-widest"
            style={{ marginBottom: "0.75rem" }}
          >
            Paused
          </PixelText>

          <ul
            role="menu"
            aria-label="Pause options"
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "0.375rem",
            }}
          >
            {ITEMS.map((item, i) => {
              const active = i === cursor;
              return (
                <li key={item.id} style={{ margin: 0 }}>
                  <button
                    type="button"
                    role="menuitem"
                    onMouseEnter={() => {
                      if (cursor !== i) {
                        setCursor(i);
                        playSfx("menu_move");
                      }
                    }}
                    onClick={() => {
                      setCursor(i);
                      void confirm(i);
                    }}
                    className="font-mono font-bold uppercase tracking-widest"
                    style={{
                      position: "relative",
                      width: "100%",
                      textAlign: "left",
                      backgroundColor: active ? INK : CREAM,
                      color: active ? CREAM : INK,
                      border: `2px solid ${INK}`,
                      padding: "0.5rem 0.75rem 0.5rem 1.75rem",
                      fontSize: "0.95rem",
                      lineHeight: 1,
                      transition: "none",
                      imageRendering: "pixelated",
                      cursor: "pointer",
                    }}
                  >
                    {active && (
                      <span
                        aria-hidden
                        style={{
                          position: "absolute",
                          left: "0.5rem",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: CREAM,
                          fontSize: "0.95rem",
                          lineHeight: 1,
                        }}
                      >
                        &#9654;
                      </span>
                    )}
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>

          {showSaveLine && (
            <div
              role="status"
              aria-live="polite"
              className="font-mono uppercase tracking-widest"
              style={{
                marginTop: "0.75rem",
                padding: "0.5rem 0.625rem",
                border: `2px solid ${INK}`,
                backgroundColor: CREAM,
                color: saveStatus.kind === "error" ? ALERT : INK,
                fontSize: "0.8rem",
                lineHeight: 1.2,
              }}
            >
              {saveStatus.kind === "saving" && "Saving..."}
              {saveStatus.kind === "ok" &&
                `Game saved to Slot ${saveStatus.slot}`}
              {saveStatus.kind === "error" && "Save failed."}
            </div>
          )}
        </div>

        {/* Side info panel */}
        <aside
          aria-label="Game status"
          style={{
            width: 180,
            backgroundColor: CREAM,
            border: `2px solid ${INK}`,
            boxShadow: `inset 1px 1px 0 ${CREAM}, inset -1px -1px 0 ${WALNUT}`,
            padding: "0.75rem 0.875rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.625rem",
          }}
        >
          <InfoRow label="Time" value={playTime ?? "—"} />
          <InfoRow label="Map" value={mapName ?? "—"} />
          <InfoRow
            label="Party"
            value={
              typeof partyCount === "number" ? String(partyCount) : "—"
            }
          />
        </aside>
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.125rem" }}>
      <span
        className="font-mono uppercase tracking-widest"
        style={{
          fontSize: "0.65rem",
          color: SAND,
          lineHeight: 1,
        }}
      >
        {label}
      </span>
      <span
        className="font-mono font-bold uppercase tracking-widest"
        style={{
          fontSize: "0.85rem",
          color: INK,
          lineHeight: 1.1,
          wordBreak: "break-word",
        }}
      >
        {value}
      </span>
    </div>
  );
}
